import * as React from "react";
import { WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import DocumentTitle from "../misc/components/DocumentTitle";
import { Query, ODataParams } from "../Components/Query";
import { EcgAnalysisSetTable, EcgAnalysisSetTableProps } from "./EcgAnalysisSetTable";
import { Route, Routes } from "react-router-dom";
import { EcgAnalysisDetail } from "./EcgAnalysisDetail";
import Modal, { ModalSize } from "../misc/components/modal";
import { EcgQcTable } from "./EcgQcTable";
import { uniqBy } from "../misc/ArrayFunctions";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";

export type SelectOption = { label: string; value: string };

interface EcgAnalysisSetListScreenProps extends EcgAnalysisSetTableProps {
    handleRefresh: (params: ODataParams) => Promise<any>;
}

export const EcgAnalysisSetListScreen = (props: EcgAnalysisSetListScreenProps) => {
    const { handleRefresh, ...rest } = props;

    return (
        <Query onRefresh={handleRefresh} initialOrdering={[{field: "analysisset/created", condition: "desc" }]}>
            <EcgAnalysisSetTable {...rest} />
        </Query>
    );
}

export const EcgAnalysisSetQCListScreen = (props: EcgAnalysisSetListScreenProps) => {
    const { handleRefresh, ...rest } = props;

    return (
        <Query onRefresh={handleRefresh} initialOrdering={[{ field: "analysisset/created", condition: "desc" }]}>
            <EcgQcTable {...rest} />
        </Query>
    );
}

export const EcgAnalysisSetScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const [analysts, setAnalysts] = React.useState<SelectOption[]>([]);
    const [analystSetTypes, setAnalystSetTypes] = React.useState<SelectOption[]>([]);
    const [showQC, setShowQC] = React.useState(false);

    const handleAnalysisSetRefresh = async (params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `analysisset/session/visit/subject/site/studyId eq ${study.id} and analysisset/type ne 'EAS_QC_OVERREAD'`;

        return await client.client.getEcgAnalysisSetTests("id,analysisSet,analysisSetId,test,accepted,analyst,analystId,analysisCompleted", filter, params.orderby, 'analysisset($expand=session($select=id,name,created,occurred,visit;$expand=visit($select=name,subject;$expand=subject($select=id,number,site,typeKey;$expand=site($select=id,number))))),test($select=id,index,created,occurred)', params.top, params.skip, undefined, true);
    }

    const handleQcRefresh = async (params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `analysisset/session/visit/subject/site/studyId eq ${study.id} and analysisset/type eq 'EAS_QC_OVERREAD'`;

        return await client.client.getEcgAnalysisSetTests("id,analysisSet,analysisSetId,test,accepted,analyst,analystId,analysisCompleted", filter, params.orderby, 'analysisset($expand=session($select=id,name,created,occurred,visit;$expand=visit($select=name,subject;$expand=subject($select=id,site;$expand=site($select=id))))),test($select=id,index,created,occurred)', params.top, params.skip, undefined, true);
    }

    const getAnalysts = async () => {
        const analysts = await client.client.getEcgAnalysisSetTests("analyst", `analysisSet/session/visit/subject/site/studyId eq ${study.id}`, "analyst");

        const options = uniqBy(analysts.value, (a, b) => a.analyst === b.analyst).filter(o => o.analyst).map(s => ({
            value: s.analyst,
            label: s.analyst,
        }));

        setAnalysts(options);
    };

    const getAnalystSetTypes = async () => {
        const types = await client.client.getEcgAnalysisSetTypes();
        const options = types.value.map(s => ({
            value: s.id,
            label: s.name,
        }));

        setAnalystSetTypes(options);
    };

    React.useEffect(() => {
            getAnalysts();
            getAnalystSetTypes();
    }, [client.client, study.id]);

    return (
        <Routes>
            <Route index element={<DocumentTitle title="ECG Analysis Sets | Vitalograph Web Portal">
                {showQC ? <EcgAnalysisSetQCListScreen handleRefresh={handleQcRefresh} analysts={analysts} analysisSetTypes={analystSetTypes} onSwitch={() => setShowQC(false)} />
                    : <EcgAnalysisSetListScreen handleRefresh={handleAnalysisSetRefresh} analysts={analysts} analysisSetTypes={analystSetTypes} onSwitch={() => setShowQC(true)} />
                }
            </DocumentTitle>} />

        </Routes>

    );
}
